import {
    ENV_PROD,
    ENV_QA,
    PAGE_TYPE_AGE_GATE,
    PAGE_TYPE_DEACTIVATION,
    PAGE_TYPE_FORM,
    PAGE_TYPE_SUCCESS,
    PROTOCOL_HTTP,
    PROTOCOL_HTTPS
} from '@bff/loader-utils';

export const PAGE_TYPE = {
    FORM: PAGE_TYPE_FORM,
    SUCCESS: PAGE_TYPE_SUCCESS,
    DEACTIVATION: PAGE_TYPE_DEACTIVATION,
    AGE_GATE: PAGE_TYPE_AGE_GATE
} as const;

export const PAGE_TYPES = [PAGE_TYPE.FORM, PAGE_TYPE.SUCCESS, PAGE_TYPE.DEACTIVATION, PAGE_TYPE.AGE_GATE] as const;
export type PageType = (typeof PAGE_TYPES)[number];

export const ENV = {
    PROD: ENV_PROD,
    QA: ENV_QA
} as const;

export const ENVIRONMENTS = [ENV.PROD, ENV.QA] as const;
export type Environment = (typeof ENVIRONMENTS)[number];

export const PROTOCOL = {
    HTTP: PROTOCOL_HTTP,
    HTTPS: PROTOCOL_HTTPS
} as const;

export const PROTOCOLS = [PROTOCOL.HTTP, PROTOCOL.HTTPS] as const;
export type Protocol = (typeof PROTOCOLS)[number];
export type FormInitializer = (container: HTMLElement) => Promise<FormBinding>;
export type RawFormInitializer = (container: HTMLElement, shouldInit: boolean) => Promise<FormBinding>;
export type InitializerKey = {
    network: string;
    property: string;
    pageId: string;
};
export type Event = {
    subscribe: (data?: unknown) => void;
};
export type FormBinding = {
    render: (pageType: PageType) => void;
    events: {
        [key: string]: Event;
    };
};

export const EVENT_TYPE = {
    AGE_GATE_LOADED: 'ageGateLoaded',
    AGE_GATE_RENDERED: 'ageGateRendered',
    CONDITIONAL_FIELD_CHANGED: 'conditionalFieldChanged',
    DEACTIVATION_LOADED: 'deactivationLoaded',
    DEACTIVATION_RENDERED: 'deactivationRendered',
    DISCOUNT_APPLIED: 'discountApplied',
    FORM_LOADED: 'formLoaded',
    FORM_RENDERED: 'formRendered',
    INELIGIBLE_LOADED: 'ineligibleLoaded',
    INELIGIBLE_RENDERED: 'ineligibleRendered',
    INPUT_CHANGED: 'inputChanged',
    PAYMENT_ACCEPTED: 'paymentAccepted',
    PAYMENT_FAILED: 'paymentFailed',
    PAYMENT_RESET: 'paymentReset',
    PAYMENT_SUBMIT_END: 'paymentSubmitEnd',
    PAYMENT_SUBMIT_START: 'paymentSubmitStart',
    RESET_ERRORS: 'resetErrors',
    SERVICE_UNAVAILABLE: 'serviceUnavailable',
    SUBMIT_END: 'submitEnd',
    SUBMIT_ERROR: 'submitError',
    SUBMIT_START: 'submitStart',
    SUBMIT_SUCCESS: 'submitSuccess',
    SUBMIT_VALIDATION_COMPLETE: 'submitValidationComplete',
    SUCCESS_LOADED: 'successLoaded',
    SUCCESS_RENDERED: 'successRendered',
    UNAVAILABLE_LOADED: 'unavailableLoaded',
    UNAVAILABLE_RENDERED: 'unavailableRendered'
} as const;

export type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];

export type EventHandlers = {
    [key in EventType]?: (data?: unknown) => void;
};

export interface FormLoadParams {
    network: string;
    property: string;
    pageId: string;
    pageType?: PageType;
    isPreview?: boolean;
    viewClone?: boolean;
    env?: Environment;
    protocol?: Protocol;
    bootstrapOnly?: boolean;
    setPageTitle?: boolean;
}

export interface FormLoadAndRenderParams extends FormLoadParams {
    container: HTMLElement
    eventHandlers?: EventHandlers;
}

export const DATA_ATTRIBUTES = {
    NETWORK: 'network',
    PROPERTY: 'property',
    PAGE_ID: 'pageId',
    PAGE_TYPE: 'pageType',
    VIEW_CLONE: 'viewClone',
    IS_PREVIEW: 'isPreview',
    BOOTSTRAP_ONLY: 'bootstrapOnly',
    PROTOCOL: 'protocol',
    SET_PAGE_TITLE: 'setPageTitle',
    ENV: 'env',
    VERSION: 'version'
} as const;

export type DataAttribute = (typeof DATA_ATTRIBUTES)[keyof typeof DATA_ATTRIBUTES];

